<template>
  <component
    :is="isCollapsed ? 'div' : EmptyWrapper"
    class="ui-grid__row"
    :class="rowClasses"
    :style="rowStyles"
  >
    <RowCell
      v-if="selectable"
      v-bind="{ active, editRow, isCollapsed, item }"
      is-first
      @click:menu="handleClickMenu"
    >
      <div
        v-if="$slots.prefix"
        class="ui-grid__row--prefix"
      >
        <slot name="prefix" v-bind="{ active, item }" />
      </div>
      <slot name="checkbox" v-bind="{ active, item }">
        <Checkbox
          ref="checkboxRef"
          v-model="selected"
          :true-value="[]"
          :value="itemKey"
        />
      </slot>
      <template #actions="slotProps">
        <slot name="actions" v-bind="slotProps" />
      </template>
    </RowCell>
    <RowCell
      v-for="(column, index) in columns"
      :key="column.name"
      ref="cellRef"
      v-bind="{ active, column, editRow, isCollapsed, item }"
      :row-actions
      :is-first="!selectable && index === 0"
      :is-last="index === props.columns.length - 1"
      :edit-cell="(edit && index === edit.cell) || undefined"
      @click="handleClickCell"
      @dblclick="handleDblClickCell(index)"
      @click:menu="handleClickMenu"
      @click:value="handleClickValue(index)"
    >
      <template #actions="slotProps">
        <slot name="actions" v-bind="slotProps" />
      </template>
      <template #default="slotProps">
        <slot
          v-if="$slots[getSlotName(column.name, 'cell')]"
          :name="getSlotName(column.name, 'cell')"
          v-bind="slotProps"
        />
        <slot v-else name="cell" v-bind="slotProps" />
      </template>
    </RowCell>
  </component>
</template>

<script setup lang="ts" generic="T">
import { computed, inject, provide, ref, useTemplateRef } from 'vue'

import { DottedMenuItem } from '@types'
import { GridColumn, GridEdit } from '../utils/types'

import { getSlotName } from '../utils/helpers'

import EmptyWrapper from './EmptyWrapper.vue'
import RowCell from './RowCell.vue'
import Checkbox from './Checkbox.vue'

type Props = {
  columns: GridColumn[]
  item: T
  itemKey: string | number

  active: boolean

  isCollapsed?: boolean

  editRow?: boolean

  edit?: GridEdit

  template?: string

  rowActions?: DottedMenuItem[]
}

type Emits = {
  'click:row': []
  'dblclick:cell': [data: T, index: number]
  'click:menu': [data: T]
  'click:value': [data: T, index: number]
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

defineOptions({
  name: 'GridRow',
})

const selected = defineModel<(string | number)[]>('selected')

const cellRef = useTemplateRef<any[]>('cellRef')
const checkboxRef = useTemplateRef('checkboxRef')

defineExpose({
  getCheckableItemKey() {
    return checkboxRef.value && props.itemKey
  },
  paste(index: number, data: string | number, check?: boolean) {
    if (!cellRef.value?.[index]) return
    cellRef.value[index].paste(data, check)
  },
})

const selectable = inject('selectable', false)

const isHovered = ref(false)
provide('isHovered', isHovered)

const rowStyles = computed(
  () => !props.isCollapsed && `grid-template-columns: ${props.template}`,
)

const checked = computed(() => selected.value?.includes(props.itemKey))
provide('isChecked', checked)

const rowClasses = computed(() => ({
  'ui-grid__row--highlighted': props.active,
}))

const handleClickCell = () => {
  emit('click:row')
}

const handleDblClickCell = (index: number) => {
  emit('dblclick:cell', props.item, index)
}

const handleClickMenu = () => {
  emit('click:menu', props.item)
}

const handleClickValue = (index: number) => {
  emit('click:value', props.item, index)
}
</script>

<style scoped>
.ui-grid__row {
  @apply relative;
  @apply my-2 py-2;
  @apply bg-body;
  @apply border border-common-border;
  @apply rounded-md;

  &--highlighted {
    @apply bg-primary-light dark:bg-opacity-40;
  }

  &--prefix {
    @apply absolute right-[calc(100%+.25rem)];
    @apply top-1/2 -translate-y-1/2;
  }
}

.ui-grid:not(.ui-grid--collapsed) {
  .ui-grid__row {
    @apply grid content-start;
  }
}
</style>
